import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { api } from 'common';
import { colors } from '../components/Theme/WebTheme';
import {
  Grid,
  Button,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  CardHeader,
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GoBackButton from '../components/GoBackButton';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(theme => ({
  container: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  card: {
    marginBottom: '20px',
    padding: '20px',
  },
  formField: {
    marginBottom: '16px',
  },
  section: {
    marginBottom: '24px',
  },
  sectionTitle: {
    marginBottom: '16px',
    color: colors.primary,
    fontWeight: 'bold',
  },
  saveButton: {
    backgroundColor: colors.primary,
    color: 'white',
    '&:hover': {
      backgroundColor: colors.primaryDark,
    },
  },
  customerInfo: {
    backgroundColor: '#f5f5f5',
    padding: '12px',
    borderRadius: '4px',
    marginBottom: '16px',
  },
  previewTable: {
    marginTop: '24px',
  },
  tableCell: {
    fontWeight: 'bold',
  },
  originalPrice: {
    textDecoration: 'line-through',
    color: '#999',
    marginRight: '8px',
  },
  newPrice: {
    color: '#2e7d32',
    fontWeight: 'bold',
  },
  discountChip: {
    backgroundColor: '#4caf50',
    color: 'white',
    fontSize: '0.75rem',
  },
  markupChip: {
    backgroundColor: '#f44336',
    color: 'white',
    fontSize: '0.75rem',
  },
  vehicleAccordion: {
    marginBottom: '16px',
  },
  vehicleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  vehicleImage: {
    width: '40px',
    height: '40px',
    objectFit: 'contain',
  },
  vehiclePricingGrid: {
    marginTop: '16px',
  },
  resetButton: {
    marginLeft: '8px',
    backgroundColor: '#f44336',
    color: 'white',
    '&:hover': {
      backgroundColor: '#d32f2f',
    },
  },
}));

const CustomPricingManagement = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation();
  const { i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { users, error: usersError } = useSelector(state => state.usersdata);
  const cartypes = useSelector(state => state.cartypes);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');

  const [pricingConfig, setPricingConfig] = useState({
    enabled: false,
    minFareOverride: 0,
    validUntil: '',
    notes: '',
    vehiclePricing: {},
  });

  // Initialize vehicle pricing from car types
  useEffect(() => {
    if (cartypes && cartypes.cars) {
      const vehiclePricing = {};
      cartypes.cars.forEach(carType => {
        vehiclePricing[carType.id] = {
          enabled: false,
          ratePerKm: parseFloat(carType.rate_per_unit_distance),
          baseFare: parseFloat(carType.base_fare),
          minFare: parseFloat(carType.min_fare),
          convenienceFees: parseFloat(carType.convenience_fees),
          convenienceFeeType: carType.convenience_fee_type || 'percentage',
        };
      });
      setPricingConfig(prev => ({
        ...prev,
        vehiclePricing,
      }));
    }
  }, [cartypes]);

  // Calculate pricing preview for each vehicle type
  const calculatePricingPreview = carType => {
    const vehicleConfig = pricingConfig.vehiclePricing[carType.id];

    if (!pricingConfig.enabled || !vehicleConfig?.enabled) {
      return {
        original: {
          ratePerKm: parseFloat(carType.rate_per_unit_distance),
          baseFare: parseFloat(carType.base_fare),
          minFare: parseFloat(carType.min_fare),
          convenienceFees: parseFloat(carType.convenience_fees),
        },
        adjusted: {
          ratePerKm: parseFloat(carType.rate_per_unit_distance),
          baseFare: parseFloat(carType.base_fare),
          minFare: parseFloat(carType.min_fare),
          convenienceFees: parseFloat(carType.convenience_fees),
        },
        changes: {
          ratePerKm: { discount: 0, isDiscount: false },
          baseFare: { discount: 0, isDiscount: false },
          minFare: { discount: 0, isDiscount: false },
          convenienceFees: { discount: 0, isDiscount: false },
        },
        hasCustomPricing: false,
      };
    }

    const original = {
      ratePerKm: parseFloat(carType.rate_per_unit_distance),
      baseFare: parseFloat(carType.base_fare),
      minFare: parseFloat(carType.min_fare),
      convenienceFees: parseFloat(carType.convenience_fees),
    };

    const adjusted = {
      ratePerKm: vehicleConfig.ratePerKm,
      baseFare: vehicleConfig.baseFare,
      minFare:
        pricingConfig.minFareOverride > 0
          ? parseFloat(pricingConfig.minFareOverride)
          : vehicleConfig.minFare,
      convenienceFees: vehicleConfig.convenienceFees,
    };

    const changes = {
      ratePerKm: {
        discount: Math.abs(
          ((original.ratePerKm - adjusted.ratePerKm) / original.ratePerKm) * 100
        ),
        isDiscount: adjusted.ratePerKm < original.ratePerKm,
      },
      baseFare: {
        discount: Math.abs(
          ((original.baseFare - adjusted.baseFare) / original.baseFare) * 100
        ),
        isDiscount: adjusted.baseFare < original.baseFare,
      },
      minFare: {
        discount: Math.abs(
          ((original.minFare - adjusted.minFare) / original.minFare) * 100
        ),
        isDiscount: adjusted.minFare < original.minFare,
      },
      convenienceFees: {
        discount: Math.abs(
          ((original.convenienceFees - adjusted.convenienceFees) /
            original.convenienceFees) *
            100
        ),
        isDiscount: adjusted.convenienceFees < original.convenienceFees,
      },
    };

    return { original, adjusted, changes, hasCustomPricing: true };
  };

  // Render price cell with before/after comparison
  const renderPriceCell = (
    original,
    adjusted,
    change,
    prefix = '',
    hasCustomPricing = false
  ) => {
    if (!hasCustomPricing || original === adjusted) {
      return (
        <span>
          {prefix}
          {original.toFixed(2)}
        </span>
      );
    }

    return (
      <Box display="flex" alignItems="center" gap={1}>
        <span className={classes.originalPrice}>
          {prefix}
          {original.toFixed(2)}
        </span>
        <span className={classes.newPrice}>
          {prefix}
          {adjusted.toFixed(2)}
        </span>
        {change.discount > 0 && (
          <Chip
            label={`${change.discount.toFixed(1)}% ${change.isDiscount ? 'off' : 'markup'}`}
            size="small"
            className={
              change.isDiscount ? classes.discountChip : classes.markupChip
            }
          />
        )}
      </Box>
    );
  };

  // Find customer by ID from URL params
  useEffect(() => {
    if (users && users.length > 0 && id) {
      const customer = users.find(
        user => user.id === id && user.usertype === 'customer'
      );
      if (customer) {
        setSelectedCustomer({
          id: customer.id,
          label: `${customer.firstName} ${customer.lastName} (${customer.email})`,
          email: customer.email,
          mobile: customer.mobile,
          userData: customer,
        });
      } else {
        setMessage('Customer not found');
        setMessageType('error');
      }
    }
  }, [users, id]);

  useEffect(() => {
    if (selectedCustomer && selectedCustomer.userData.customPricing) {
      const customPricing = selectedCustomer.userData.customPricing;
      setPricingConfig({
        enabled: customPricing.enabled || false,
        minFareOverride: customPricing.minFareOverride || 0,
        validUntil: customPricing.validUntil || '',
        notes: customPricing.notes || '',
        vehiclePricing: customPricing.vehiclePricing || {},
      });
    } else {
      // Reset to default when no customer selected or no custom pricing
      if (cartypes && cartypes.cars) {
        const vehiclePricing = {};
        cartypes.cars.forEach(carType => {
          vehiclePricing[carType.id] = {
            enabled: false,
            ratePerKm: parseFloat(carType.rate_per_unit_distance),
            baseFare: parseFloat(carType.base_fare),
            minFare: parseFloat(carType.min_fare),
            convenienceFees: parseFloat(carType.convenience_fees),
            convenienceFeeType: carType.convenience_fee_type || 'percentage',
          };
        });
        setPricingConfig({
          enabled: false,
          minFareOverride: 0,
          validUntil: '',
          notes: '',
          vehiclePricing,
        });
      }
    }
  }, [selectedCustomer, cartypes]);

  // Handle Redux state changes for user feedback
  useEffect(() => {
    if (usersError && usersError.flag && loading) {
      setMessage('Error updating pricing: ' + usersError.msg);
      setMessageType('error');
      setLoading(false);
    }
  }, [usersError, loading]);

  const handlePricingChange = (field, value) => {
    setPricingConfig(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleVehiclePricingChange = (vehicleId, field, value) => {
    setPricingConfig(prev => ({
      ...prev,
      vehiclePricing: {
        ...prev.vehiclePricing,
        [vehicleId]: {
          ...prev.vehiclePricing[vehicleId],
          [field]: value,
        },
      },
    }));
  };

  const resetVehiclePricing = vehicleId => {
    const carType = cartypes.cars.find(car => car.id === vehicleId);
    if (carType) {
      handleVehiclePricingChange(vehicleId, 'enabled', false);
      handleVehiclePricingChange(
        vehicleId,
        'ratePerKm',
        parseFloat(carType.rate_per_unit_distance)
      );
      handleVehiclePricingChange(
        vehicleId,
        'baseFare',
        parseFloat(carType.base_fare)
      );
      handleVehiclePricingChange(
        vehicleId,
        'minFare',
        parseFloat(carType.min_fare)
      );
      handleVehiclePricingChange(
        vehicleId,
        'convenienceFees',
        parseFloat(carType.convenience_fees)
      );
      handleVehiclePricingChange(
        vehicleId,
        'convenienceFeeType',
        carType.convenience_fee_type || 'percentage'
      );
    }
  };

  const handleSave = async () => {
    if (!selectedCustomer) {
      setMessage('Please select a customer first');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      // Only send the customPricing data to avoid overwriting other user data
      const updateData = {
        customPricing: {
          ...pricingConfig,
          lastUpdated: new Date().toISOString(),
          updatedBy: 'admin', // You can get this from auth context
        },
      };

      // Dispatch the action and wait for it to complete
      dispatch(api.editUser(selectedCustomer.id, updateData));

      // Give it a moment to process
      await new Promise(resolve => setTimeout(resolve, 1000));

      setMessage('Custom pricing updated successfully!');
      setMessageType('success');

      // Refresh user data to show updated pricing
      dispatch(api.fetchUsers());
    } catch (error) {
      console.error('Error updating pricing:', error);
      setMessage('Error updating pricing: ' + error.message);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePricing = async () => {
    if (!selectedCustomer) return;

    setLoading(true);
    setMessage('');

    try {
      const updateData = {
        customPricing: null,
      };

      // Dispatch the action
      dispatch(api.editUser(selectedCustomer.id, updateData));

      // Give it a moment to process
      await new Promise(resolve => setTimeout(resolve, 1000));

      setMessage('Custom pricing removed successfully!');
      setMessageType('success');

      // Reset form
      if (cartypes && cartypes.cars) {
        const vehiclePricing = {};
        cartypes.cars.forEach(carType => {
          vehiclePricing[carType.id] = {
            enabled: false,
            ratePerKm: parseFloat(carType.rate_per_unit_distance),
            baseFare: parseFloat(carType.base_fare),
            minFare: parseFloat(carType.min_fare),
            convenienceFees: parseFloat(carType.convenience_fees),
            convenienceFeeType: carType.convenience_fee_type || 'percentage',
          };
        });
        setPricingConfig({
          enabled: false,
          minFareOverride: 0,
          validUntil: '',
          notes: '',
          vehiclePricing,
        });
      }

      // Refresh user data
      dispatch(api.fetchUsers());
    } catch (error) {
      console.error('Error removing pricing:', error);
      setMessage('Error removing pricing: ' + error.message);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  // Show loading while users are being fetched
  if (!users || users.length === 0) {
    return (
      <div className={classes.container}>
        <GoBackButton
          isRTL={isRTL}
          onClick={() => {
            navigate('/users/0', { state: { pageNo: state?.pageNo } });
          }}
        />
        <Typography variant="h4" className={classes.sectionTitle}>
          Custom Pricing Management
        </Typography>
        <Typography>Loading customer data...</Typography>
      </div>
    );
  }

  // Show error if customer not found
  if (!selectedCustomer && users && users.length > 0) {
    return (
      <div className={classes.container}>
        <GoBackButton
          isRTL={isRTL}
          onClick={() => {
            navigate('/users/0', { state: { pageNo: state?.pageNo } });
          }}
        />
        <Typography variant="h4" className={classes.sectionTitle}>
          Custom Pricing Management
        </Typography>
        <Alert severity="error">
          Customer not found. Please check the customer ID and try again.
        </Alert>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <GoBackButton
        isRTL={isRTL}
        onClick={() => {
          navigate('/users/0', { state: { pageNo: state?.pageNo } });
        }}
      />

      <Typography variant="h4" className={classes.sectionTitle}>
        Custom Pricing Management
      </Typography>

      {message && (
        <Alert severity={messageType} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}

      {selectedCustomer && (
        <Card className={classes.card}>
          <CardHeader title="Customer Information" />
          <CardContent>
            <div className={classes.customerInfo}>
              <Typography variant="h6">
                {selectedCustomer.userData.firstName}{' '}
                {selectedCustomer.userData.lastName}
              </Typography>
              <Typography>Email: {selectedCustomer.email}</Typography>
              <Typography>Mobile: {selectedCustomer.mobile}</Typography>
              <Typography>
                Current Pricing:{' '}
                {selectedCustomer.userData.customPricing?.enabled
                  ? 'Custom'
                  : 'Standard'}
              </Typography>

              {/* Enhanced pricing status display */}
              {selectedCustomer.userData.customPricing?.enabled && (
                <>
                  <Typography variant="body2" color="textSecondary">
                    Last Updated:{' '}
                    {selectedCustomer.userData.customPricing.lastUpdated
                      ? new Date(
                          selectedCustomer.userData.customPricing.lastUpdated
                        ).toLocaleDateString()
                      : 'Unknown'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Updated By:{' '}
                    {selectedCustomer.userData.customPricing.updatedBy ||
                      'Unknown'}
                  </Typography>
                  {selectedCustomer.userData.customPricing.validUntil && (
                    <Typography variant="body2" color="textSecondary">
                      Valid Until:{' '}
                      {new Date(
                        selectedCustomer.userData.customPricing.validUntil
                      ).toLocaleDateString()}
                    </Typography>
                  )}
                  {selectedCustomer.userData.customPricing.notes && (
                    <Typography variant="body2" color="textSecondary">
                      Notes: {selectedCustomer.userData.customPricing.notes}
                    </Typography>
                  )}

                  {/* Show active vehicle pricing count */}
                  <Typography variant="body2" color="primary">
                    Active Vehicle Pricing:{' '}
                    {
                      Object.values(
                        selectedCustomer.userData.customPricing
                          .vehiclePricing || {}
                      ).filter(vehicle => vehicle.enabled).length
                    }{' '}
                    / {cartypes?.cars?.length || 0} vehicles
                  </Typography>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {selectedCustomer && (
        <Card className={classes.card}>
          <CardHeader title="Pricing Configuration" />
          <CardContent>
            <FormControlLabel
              control={
                <Switch
                  checked={pricingConfig.enabled}
                  onChange={e =>
                    handlePricingChange('enabled', e.target.checked)
                  }
                  color="primary"
                />
              }
              label="Enable Custom Pricing"
              className={classes.formField}
            />

            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Vehicle Pricing
                </Typography>

                {cartypes &&
                  cartypes.cars &&
                  cartypes.cars.map(carType => (
                    <Accordion
                      key={carType.id}
                      className={classes.vehicleAccordion}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <div className={classes.vehicleHeader}>
                          <img
                            src={carType.image}
                            alt={carType.name}
                            className={classes.vehicleImage}
                          />
                          <div>
                            <Typography variant="h6">{carType.name}</Typography>
                            <Typography variant="body2" color="textSecondary">
                              {pricingConfig.vehiclePricing[carType.id]?.enabled
                                ? 'Custom pricing enabled'
                                : 'Using standard pricing'}
                            </Typography>
                          </div>
                        </div>
                      </AccordionSummary>
                      <AccordionDetails>
                        <div style={{ width: '100%' }}>
                          <Box
                            display="flex"
                            alignItems="center"
                            marginBottom={2}
                          >
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={
                                    pricingConfig.vehiclePricing[carType.id]
                                      ?.enabled || false
                                  }
                                  onChange={e =>
                                    handleVehiclePricingChange(
                                      carType.id,
                                      'enabled',
                                      e.target.checked
                                    )
                                  }
                                  color="primary"
                                />
                              }
                              label="Enable custom pricing for this vehicle"
                            />
                            <Button
                              variant="outlined"
                              size="small"
                              className={classes.resetButton}
                              onClick={() => resetVehiclePricing(carType.id)}
                            >
                              Reset to Standard
                            </Button>
                          </Box>

                          <Grid
                            container
                            spacing={2}
                            className={classes.vehiclePricingGrid}
                          >
                            <Grid item xs={6}>
                              <TextField
                                label="Rate per KM"
                                type="number"
                                inputProps={{ step: 0.1, min: 0 }}
                                value={
                                  pricingConfig.vehiclePricing[carType.id]
                                    ?.ratePerKm || 0
                                }
                                onChange={e =>
                                  handleVehiclePricingChange(
                                    carType.id,
                                    'ratePerKm',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                fullWidth
                                disabled={
                                  !pricingConfig.vehiclePricing[carType.id]
                                    ?.enabled
                                }
                                helperText={`Standard: KSH ${carType.rate_per_unit_distance}`}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Base Fare"
                                type="number"
                                inputProps={{ step: 0.1, min: 0 }}
                                value={
                                  pricingConfig.vehiclePricing[carType.id]
                                    ?.baseFare || 0
                                }
                                onChange={e =>
                                  handleVehiclePricingChange(
                                    carType.id,
                                    'baseFare',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                fullWidth
                                disabled={
                                  !pricingConfig.vehiclePricing[carType.id]
                                    ?.enabled
                                }
                                helperText={`Standard: KSH ${carType.base_fare}`}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Minimum Fare"
                                type="number"
                                inputProps={{ step: 0.1, min: 0 }}
                                value={
                                  pricingConfig.vehiclePricing[carType.id]
                                    ?.minFare || 0
                                }
                                onChange={e =>
                                  handleVehiclePricingChange(
                                    carType.id,
                                    'minFare',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                fullWidth
                                disabled={
                                  !pricingConfig.vehiclePricing[carType.id]
                                    ?.enabled
                                }
                                helperText={`Standard: KSH ${carType.min_fare}`}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <FormControl fullWidth>
                                <InputLabel
                                  id={`commission-type-label-${carType.id}`}
                                >
                                  Commission Type
                                </InputLabel>
                                <Select
                                  labelId={`commission-type-label-${carType.id}`}
                                  id={`commission-type-select-${carType.id}`}
                                  value={
                                    pricingConfig.vehiclePricing[carType.id]
                                      ?.convenienceFeeType ||
                                    carType.convenience_fee_type ||
                                    'percentage'
                                  }
                                  label="Commission Type"
                                  onChange={e =>
                                    handleVehiclePricingChange(
                                      carType.id,
                                      'convenienceFeeType',
                                      e.target.value
                                    )
                                  }
                                  disabled={
                                    !pricingConfig.vehiclePricing[carType.id]
                                      ?.enabled
                                  }
                                >
                                  <MenuItem value="percentage">
                                    Percentage (%)
                                  </MenuItem>
                                  <MenuItem value="flat">Flat Amount</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label={
                                  pricingConfig.vehiclePricing[carType.id]
                                    ?.convenienceFeeType === 'flat'
                                    ? 'Platform Commission (Flat)'
                                    : 'Platform Commission (%)'
                                }
                                type="number"
                                inputProps={{
                                  step: 0.1,
                                  min: 0,
                                  max:
                                    pricingConfig.vehiclePricing[carType.id]
                                      ?.convenienceFeeType === 'flat'
                                      ? undefined
                                      : 100,
                                }}
                                value={
                                  pricingConfig.vehiclePricing[carType.id]
                                    ?.convenienceFees || 0
                                }
                                onChange={e =>
                                  handleVehiclePricingChange(
                                    carType.id,
                                    'convenienceFees',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                fullWidth
                                disabled={
                                  !pricingConfig.vehiclePricing[carType.id]
                                    ?.enabled
                                }
                                helperText={
                                  pricingConfig.vehiclePricing[carType.id]
                                    ?.convenienceFeeType === 'flat'
                                    ? `Standard: KSH ${carType.convenience_fees}`
                                    : `Standard: ${carType.convenience_fees}%`
                                }
                              />
                            </Grid>
                          </Grid>
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  ))}
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Special Settings
                </Typography>

                <TextField
                  label="Minimum Fare Override"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={pricingConfig.minFareOverride}
                  onChange={e =>
                    handlePricingChange(
                      'minFareOverride',
                      parseFloat(e.target.value) || 0
                    )
                  }
                  fullWidth
                  className={classes.formField}
                  helperText="Leave 0 to use vehicle-specific minimum fare"
                />

                <TextField
                  label="Valid Until"
                  type="date"
                  value={pricingConfig.validUntil}
                  onChange={e =>
                    handlePricingChange('validUntil', e.target.value)
                  }
                  fullWidth
                  className={classes.formField}
                  InputLabelProps={{ shrink: true }}
                  helperText="Leave empty for no expiry"
                />

                <TextField
                  label="Notes"
                  multiline
                  rows={4}
                  value={pricingConfig.notes}
                  onChange={e => handlePricingChange('notes', e.target.value)}
                  fullWidth
                  className={classes.formField}
                  helperText="Internal notes about this pricing configuration"
                />

                <Button
                  variant="contained"
                  fullWidth
                  className={classes.saveButton}
                  onClick={handleSave}
                  disabled={loading || !selectedCustomer}
                  style={{ marginTop: '16px' }}
                >
                  {loading ? 'Saving...' : 'Save Custom Pricing'}
                </Button>

                {selectedCustomer?.userData?.customPricing?.enabled && (
                  <Button
                    variant="outlined"
                    fullWidth
                    color="error"
                    onClick={handleRemovePricing}
                    disabled={loading || !selectedCustomer}
                    style={{ marginTop: '8px' }}
                  >
                    {loading ? 'Removing...' : 'Remove Custom Pricing'}
                  </Button>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Pricing Preview Table */}
      {selectedCustomer &&
        cartypes &&
        cartypes.cars &&
        cartypes.cars.length > 0 && (
          <Card className={classes.previewTable}>
            <CardHeader title="Pricing Preview - How Rates Change for Each Vehicle Type" />
            <CardContent>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableCell}>
                        Vehicle Type
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        Rate per KM
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        Base Fare
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        Minimum Fare
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        Convenience Fees
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cartypes.cars.map(carType => {
                      const preview = calculatePricingPreview(carType);
                      return (
                        <TableRow key={carType.id}>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <img
                                src={carType.image}
                                alt={carType.name}
                                style={{
                                  width: 40,
                                  height: 40,
                                  objectFit: 'contain',
                                }}
                              />
                              <Typography variant="body2" fontWeight="bold">
                                {carType.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {renderPriceCell(
                              preview.original.ratePerKm,
                              preview.adjusted.ratePerKm,
                              preview.changes.ratePerKm,
                              'KSH ',
                              preview.hasCustomPricing
                            )}
                          </TableCell>
                          <TableCell>
                            {renderPriceCell(
                              preview.original.baseFare,
                              preview.adjusted.baseFare,
                              preview.changes.baseFare,
                              'KSH ',
                              preview.hasCustomPricing
                            )}
                          </TableCell>
                          <TableCell>
                            {renderPriceCell(
                              preview.original.minFare,
                              preview.adjusted.minFare,
                              preview.changes.minFare,
                              'KSH ',
                              preview.hasCustomPricing
                            )}
                          </TableCell>
                          <TableCell>
                            {renderPriceCell(
                              preview.original.convenienceFees,
                              preview.adjusted.convenienceFees,
                              preview.changes.convenienceFees,
                              carType.convenience_fee_type === 'flat'
                                ? 'KSH '
                                : '',
                              preview.hasCustomPricing
                            )}
                            {carType.convenience_fee_type === 'percentage' &&
                              '%'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {!pricingConfig.enabled && (
                <Box mt={2}>
                  <Alert severity="info">
                    Enable custom pricing above to see how rates will change for
                    this customer.
                  </Alert>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
    </div>
  );
};

export default CustomPricingManagement;
